import type { LucideIcon } from "lucide-react";
import { BookMarked, ChartColumn, Columns3, Library, Settings, Smartphone, Sun } from "lucide-react";

export type RouteId = "today" | "inbox" | "research" | "kanban" | "review" | "metrics" | "settings";

export type NavGroupId = "topics" | "make" | "ops";

export interface NavItem {
  id: RouteId;
  path: string;
  icon: LucideIcon;
}

export const HOME_ITEM: NavItem = { id: "today", path: "/", icon: Sun };

export const NAV_GROUPS: ReadonlyArray<{ id: NavGroupId; items: readonly NavItem[] }> = [
  {
    id: "topics",
    items: [
      { id: "inbox", path: "/inbox", icon: Library },
      { id: "research", path: "/research", icon: BookMarked },
    ],
  },
  {
    id: "make",
    items: [
      { id: "kanban", path: "/kanban", icon: Columns3 },
      { id: "review", path: "/review", icon: Smartphone },
    ],
  },
  {
    id: "ops",
    items: [
      { id: "metrics", path: "/metrics", icon: ChartColumn },
      { id: "settings", path: "/settings", icon: Settings },
    ],
  },
];

const PATH_FOR: Record<RouteId, string> = {
  today: "/",
  inbox: "/inbox",
  research: "/research",
  kanban: "/kanban",
  review: "/review",
  metrics: "/metrics",
  settings: "/settings",
};

export interface LocationTarget {
  route: RouteId;
  search: string;
}

export function pathFor(route: RouteId): string {
  return PATH_FOR[route];
}

export function hrefFor(target: LocationTarget): string {
  return `${pathFor(target.route)}${target.search ? `?${target.search}` : ""}`;
}

const SETTING_SECTIONS = new Set(["executors", "account", "notion", "find"]);

/** Old paths land on the restored pages. The address bar is rewritten to the canonical path. */
export function resolveLocation(pathname: string, search: string): LocationTarget {
  const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);

  if (path === "/" || path === "/today") return { route: "today", search: kept(params, ["topic", "find"]) };
  if (path === "/find") return { route: "inbox", search: "tab=find" };
  if (path === "/inbox") return { route: "inbox", search: kept(params, ["status", "task", "due", "tab"]) };
  if (path === "/research") return { route: "research", search: researchSearch(params) };
  if (path === "/board") {
    params.set("view", "list");
    params.set("status", "制作中");
    params.delete("calendar");
    return { route: "kanban", search: kanbanSearch(params) };
  }
  if (path === "/calendar" || path === "/publish") {
    params.set("view", "calendar");
    params.delete("calendar");
    return { route: "kanban", search: kanbanSearch(params) };
  }
  if (path === "/kanban" || path === "/content") return { route: "kanban", search: kanbanSearch(params) };
  if (path === "/review") return { route: "review", search: kept(params, ["topic"]) };
  if (path === "/metrics") return { route: "metrics", search: "" };
  if (path === "/tasks" || path === "/executors") return { route: "settings", search: "section=executors" };
  if (path === "/accounts") return { route: "settings", search: "section=account" };
  if (path === "/settings") return settingsTarget(params);
  return { route: "today", search: "" };
}

function researchSearch(params: URLSearchParams): string {
  const part = params.get("part");
  const section = params.get("section");
  const next = part === "style" || part === "score" ? part : section === "style" ? "style" : section === "pass" || section === "score" ? "score" : "";
  return next ? `part=${next}` : "";
}

const KANBAN_STATUSES = new Set(["待确认", "制作中", "待审阅", "待发布", "已发布"]);

function kanbanSearch(params: URLSearchParams): string {
  const next = new URLSearchParams();
  let view = params.get("view");
  let status = params.get("status");
  if (params.get("calendar") === "1") view = "calendar";
  if (view === "queue") {
    view = "list";
    status = "制作中";
  }
  if (view === "list" || view === "board" || view === "calendar") next.set("view", view);
  if (status && KANBAN_STATUSES.has(status)) next.set("status", status);
  const topic = params.get("topic");
  if (topic) next.set("topic", topic);
  return next.toString();
}

function settingsTarget(params: URLSearchParams): LocationTarget {
  const section = params.get("section");
  if (section === "style") return { route: "research", search: "part=style" };
  if (section === "notes") return { route: "research", search: "part=notes" };
  if (section === "pass" || section === "score") return { route: "research", search: "part=score" };
  if (section && SETTING_SECTIONS.has(section)) return { route: "settings", search: `section=${section}` };
  return { route: "settings", search: "" };
}

function kept(params: URLSearchParams, keys: readonly string[]): string {
  const next = new URLSearchParams();
  for (const key of keys) {
    const value = params.get(key);
    if (value) next.set(key, value);
  }
  return next.toString();
}

export type NavIcon = LucideIcon;
